<script setup lang="ts">
  import { InboundEmailsService, type SuperUserInboundEmail } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type Filter,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import { ROUTES } from '@/router/routes'
  import { formatDateTimeGerman } from '../../../lib/util/formatting'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { TableCell, TableRow } from '@/components/ui/table'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-vue-next'
  import { ref, nextTick } from 'vue'

  const tableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  const sortField = ref('created_at')
  const sortOrder = ref<'asc' | 'desc'>('desc')

  const handleSort = (field: string) => {
    if (sortField.value === field) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    } else {
      sortField.value = field
      sortOrder.value = 'asc'
    }
    nextTick(() => tableRef.value?.resetAndRefresh())
  }

  const getSortIcon = (field: string) => {
    if (sortField.value !== field) return ArrowUpDown
    return sortOrder.value === 'asc' ? ArrowUp : ArrowDown
  }

  const filterableFields: Filter[] = [
    {
      key: 'isFailed',
      label: 'Failed',
      type: 'select',
      options: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' }
      ]
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      type: 'date-range'
    }
  ]

  const fetchInboundEmails = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<unknown>> => {
    const filters = options?.filters || []
    const getFilter = (key: string) => filters.find((f) => f.key === key)?.value
    const dateRangeValue = getFilter('dateRange')
    let startDate: string | undefined
    let endDate: string | undefined
    if (dateRangeValue) {
      const parts = dateRangeValue.split(',')
      if (parts.length === 2) {
        startDate = parts[0]?.trim() || undefined
        endDate = parts[1]?.trim() || undefined
      }
    }

    const failedVal = getFilter('isFailed')
    const ordering = sortOrder.value === 'desc' ? `-${sortField.value}` : sortField.value

    const response = await InboundEmailsService.inboundEmailsList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
      isFailed: failedVal === undefined ? undefined : failedVal === 'true',
      startDate,
      endDate,
      ordering
    })
    return {
      results: response.results as unknown[],
      next: response.next ?? null,
      previous: response.previous ?? null,
      count: response.count
    }
  }
</script>
<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Emails', to: ROUTES.ADMIN.EMAIL.INBOUND_EMAIL_LIST }
          ]"
        />
      </div>
      <!-- End Page Header -->

      <!-- Users Table Card -->
      <div class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div>
          <!-- Tab Content -->
          <div class="">
            <!-- Table Section -->
            <div
              class="[&amp;::-webkit-scrollbar]:h-2 [&amp;::-webkit-scrollbar-thumb]:rounded-full
                [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300
                &amp;::-webkit-scrollbar-track]:bg-neutral-700 &amp;::-webkit-scrollbar-thumb]:bg-neutral-500
                overflow-x-auto"
            >
              <div class="inline-block min-w-full align-middle">
                <!-- Table -->
                <PaginatedTableView
                  ref="tableRef"
                  :fetchData="fetchInboundEmails"
                  searchableFields="From, To, Subject, Content"
                  :filterableFields="filterableFields"
                >
                  <template #table-head>
                    <TableCell>UUID</TableCell>
                    <TableCell
                      class="cursor-pointer select-none"
                      @click="handleSort('created_at')"
                    >
                      <div class="inline-flex items-center gap-1">
                        Created
                        <component
                          :is="getSortIcon('created_at')"
                          class="h-4 w-4"
                        />
                      </div>
                    </TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Failed</TableCell>
                    <TableCell></TableCell>
                  </template>
                  <template #table-row="{ items: emails }">
                    <TableRow
                      v-for="email in emails"
                      :key="email.uuid"
                    >
                      <UniqueTableDataCell :truncate="true">
                        <router-link
                          :to="ROUTES.ADMIN.EMAIL.INBOUND_EMAIL_DETAIL.replace(':uuid', email.uuid)"
                        >
                          {{ email.uuid }}
                        </router-link>
                      </UniqueTableDataCell>
                      <TableCell>
                        {{ email.created_at ? formatDateTimeGerman(email.created_at) : '-' }}
                      </TableCell>
                      <TableCell>
                        {{ email.from_email.email }}
                      </TableCell>
                      <TableCell>
                        <span
                          v-for="to in email.to"
                          :key="to.email"
                        >
                          {{ to.email }}
                        </span>
                      </TableCell>
                      <TableCell class="max-w-[300px] truncate">
                        {{ email.subject || '-' }}
                      </TableCell>
                      <TableCell>
                        {{ email.is_failed ? 'Yes' : 'No' }}
                      </TableCell>
                      <TableCell>
                        <router-link
                          class="inline-flex items-center gap-x-2 rounded-lg border border-transparent text-sm font-semibold
                            text-blue-600 hover:text-blue-800 disabled:pointer-events-none disabled:opacity-50
                            dark:text-blue-500 dark:hover:text-blue-400"
                          :to="ROUTES.ADMIN.EMAIL.INBOUND_EMAIL_DETAIL.replace(':uuid', email.uuid)"
                          type="button"
                        >
                          Details
                        </router-link>
                      </TableCell>
                    </TableRow>
                  </template>
                </PaginatedTableView>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
